import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { withLatestFrom, switchMap, map, catchError, of } from "rxjs";
import { selectSelectedCompany } from "../../../../user/user.selectors";
import { CatalogDatasetsActions } from "./catalog-datasets.actions";
import { DataEcoLabelsActions } from "../../../../application/data/eco-label/eco-labels/eco-labels.actions";
import { DataUnspscsActions } from "../../../../application/data/unspsc/unspscs/unspscs.actions";
import { EcoLabel, EcoLabelClient, UnspscClient } from "src/api-service";
import { normalizeError } from "../../../../app-store-error.types";

@Injectable()
export class CatalogDatasetsEffects {
    private readonly actions$ = inject(Actions);
    private readonly store = inject(Store);
    private readonly ecoLabelClient = inject(EcoLabelClient);
    private readonly unspscClient = inject(UnspscClient);

    // =========================================================================
    // ECO LABELS CRUD
    // =========================================================================
    createEcoLabel$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CatalogDatasetsActions.createEcoLabel),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([{ request }, selectedCompany]) =>
                this.ecoLabelClient.create(selectedCompany.id, request).pipe(
                    map(ecoLabel =>
                        CatalogDatasetsActions.createEcoLabelSuccess({
                            ecoLabel,
                            toastKey: "ADMIN.SUPER_USER.DATASETS.ECO_LABELS.TOAST.CREATE_SUCCESS",
                        }),
                    ),
                    catchError(error =>
                        of(
                            CatalogDatasetsActions.createEcoLabelFailure({
                                error: normalizeError(error),
                                toastKey: "ADMIN.SUPER_USER.DATASETS.ECO_LABELS.TOAST.CREATE_ERROR",
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    updateEcoLabel$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CatalogDatasetsActions.updateEcoLabel),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([{ id, request }, selectedCompany]) =>
                this.ecoLabelClient.update(id, request, selectedCompany.id).pipe(
                    map(ecoLabel =>
                        CatalogDatasetsActions.updateEcoLabelSuccess({
                            ecoLabel,
                            toastKey: "ADMIN.SUPER_USER.DATASETS.ECO_LABELS.TOAST.UPDATE_SUCCESS",
                        }),
                    ),
                    catchError(error =>
                        of(
                            CatalogDatasetsActions.updateEcoLabelFailure({
                                error: normalizeError(error),
                                toastKey: "ADMIN.SUPER_USER.DATASETS.ECO_LABELS.TOAST.UPDATE_ERROR",
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    deleteEcoLabel$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CatalogDatasetsActions.deleteEcoLabel),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([{ id }, selectedCompany]) =>
                this.ecoLabelClient.delete(id, selectedCompany.id).pipe(
                    map(() =>
                        CatalogDatasetsActions.deleteEcoLabelSuccess({
                            id,
                            toastKey: "ADMIN.SUPER_USER.DATASETS.ECO_LABELS.TOAST.DELETE_SUCCESS",
                        }),
                    ),
                    catchError(error =>
                        of(
                            CatalogDatasetsActions.deleteEcoLabelFailure({
                                error: normalizeError(error),
                                toastKey: "ADMIN.SUPER_USER.DATASETS.ECO_LABELS.TOAST.DELETE_ERROR",
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    uploadEcoLabelImage$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CatalogDatasetsActions.uploadEcoLabelImage),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([{ id, file }, selectedCompany]) =>
                this.ecoLabelClient.uploadImage(id, selectedCompany.id, { data: file, fileName: file.name }).pipe(
                    map(ecoLabel =>
                        CatalogDatasetsActions.uploadEcoLabelImageSuccess({
                            ecoLabel,
                            toastKey: "ADMIN.SUPER_USER.DATASETS.ECO_LABELS.TOAST.IMAGE_UPLOAD_SUCCESS",
                        }),
                    ),
                    catchError(error =>
                        of(
                            CatalogDatasetsActions.uploadEcoLabelImageFailure({
                                error: normalizeError(error),
                                toastKey: "ADMIN.SUPER_USER.DATASETS.ECO_LABELS.TOAST.IMAGE_UPLOAD_ERROR",
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    deleteEcoLabelImage$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CatalogDatasetsActions.deleteEcoLabelImage),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([{ id }, selectedCompany]) =>
                this.ecoLabelClient.deleteImage(id, selectedCompany.id).pipe(
                    map(() =>
                        CatalogDatasetsActions.deleteEcoLabelImageSuccess({
                            ecoLabel: { id, imageUrl: undefined } as EcoLabel,
                            toastKey: "ADMIN.SUPER_USER.DATASETS.ECO_LABELS.TOAST.IMAGE_DELETE_SUCCESS",
                        }),
                    ),
                    catchError(error =>
                        of(
                            CatalogDatasetsActions.deleteEcoLabelImageFailure({
                                error: normalizeError(error),
                                toastKey: "ADMIN.SUPER_USER.DATASETS.ECO_LABELS.TOAST.IMAGE_DELETE_ERROR",
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    // Reload global eco labels store after any successful CRUD operation
    reloadEcoLabelsAfterCrud$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                CatalogDatasetsActions.createEcoLabelSuccess,
                CatalogDatasetsActions.updateEcoLabelSuccess,
                CatalogDatasetsActions.deleteEcoLabelSuccess,
                CatalogDatasetsActions.uploadEcoLabelImageSuccess,
                CatalogDatasetsActions.deleteEcoLabelImageSuccess,
            ),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            map(([, selectedCompany]) => DataEcoLabelsActions.load({ companyId: selectedCompany.id })),
        ),
    );

    // =========================================================================
    // UNSPSC CRUD
    // =========================================================================
    createUnspsc$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CatalogDatasetsActions.createUnspsc),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([{ request }, selectedCompany]) =>
                this.unspscClient.create(selectedCompany.id, request).pipe(
                    map(unspsc =>
                        CatalogDatasetsActions.createUnspscSuccess({
                            unspsc,
                            toastKey: "ADMIN.SUPER_USER.DATASETS.UNSPSC.TOAST.CREATE_SUCCESS",
                        }),
                    ),
                    catchError(error =>
                        of(
                            CatalogDatasetsActions.createUnspscFailure({
                                error: normalizeError(error),
                                toastKey: "ADMIN.SUPER_USER.DATASETS.UNSPSC.TOAST.CREATE_ERROR",
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    updateUnspsc$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CatalogDatasetsActions.updateUnspsc),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([{ id, request }, selectedCompany]) =>
                this.unspscClient.update(id, request, selectedCompany.id).pipe(
                    map(unspsc =>
                        CatalogDatasetsActions.updateUnspscSuccess({
                            unspsc,
                            toastKey: "ADMIN.SUPER_USER.DATASETS.UNSPSC.TOAST.UPDATE_SUCCESS",
                        }),
                    ),
                    catchError(error =>
                        of(
                            CatalogDatasetsActions.updateUnspscFailure({
                                error: normalizeError(error),
                                toastKey: "ADMIN.SUPER_USER.DATASETS.UNSPSC.TOAST.UPDATE_ERROR",
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    deleteUnspsc$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CatalogDatasetsActions.deleteUnspsc),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([{ id }, selectedCompany]) =>
                this.unspscClient.delete(id, selectedCompany.id).pipe(
                    map(() =>
                        CatalogDatasetsActions.deleteUnspscSuccess({
                            id,
                            toastKey: "ADMIN.SUPER_USER.DATASETS.UNSPSC.TOAST.DELETE_SUCCESS",
                        }),
                    ),
                    catchError(error =>
                        of(
                            CatalogDatasetsActions.deleteUnspscFailure({
                                error: normalizeError(error),
                                toastKey: "ADMIN.SUPER_USER.DATASETS.UNSPSC.TOAST.DELETE_ERROR",
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    // Reload global unspscs store after any successful CRUD operation
    reloadUnspscsAfterCrud$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                CatalogDatasetsActions.createUnspscSuccess,
                CatalogDatasetsActions.updateUnspscSuccess,
                CatalogDatasetsActions.deleteUnspscSuccess,
            ),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            map(([, selectedCompany]) => DataUnspscsActions.load({ companyId: selectedCompany.id })),
        ),
    );
}
